import dayjs from 'dayjs'
import minMax from 'dayjs/plugin/minMax'
import Decimal from 'decimal.js'
import {
  getComponentRadius,
  getManufactureSpecsForBin,
  getMostRecentPlateInBin,
  isInAnnularRing,
} from 'features/assetModeling'
import { calculateCorrosionMultiplier } from 'features/dataLayers/generation/calculations/calculateCorrosionRateMultiplier'
import { isEmpty, isNil, sum } from 'lodash'
import {
  CustomCorrosionRegion,
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoCoatingRegion,
  GeckoComponentCoords,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoConeCourse,
  GeckoFeatureModelType,
  GeckoHorizontalTankShell,
  GeckoHorizontalWeld,
  GeckoLegacyBoilerWall,
  GeckoLegacyBoilerWalls,
  GeckoLegacyCone,
  GeckoLinerRegion,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPipeSection,
  GeckoPlate,
  GeckoShellCourse,
  GeckoTankFlatRoof,
  GeckoTankFloor,
  GeckoTMinMethod,
  GeckoVerticalTankShell,
  GeckoVerticalWeld,
  OverrideRegion,
  PipeWrapRegion,
  SpecificGravityProfileEnum,
} from 'types'

import { CalculateBinValueFunction } from './calculateDerivedValuesHelpers'
import { getTminByApi570BinValueFunction } from './calculateDerivedValuesPipe'
import { interpolateGravity } from './calculateInterpolatedSpecificGravity'

dayjs.extend(minMax)

// ----------------------------------------------------------------------------------------
/**
 * This file organizes the functions that calculate derived values (Remaining Life, Loss, T_min, etc.)
 *
 *  For optimum performance all the functions in this file should convert to Metric units before performing calculations,
 *  and should only return values in Metric units: we use Metric because that is how data in the RAM is stored, so if
 *  this practice is followed there should be fewer conversions performed as bin-value functions call each-other.
 *
 *  While the decision to always use metric is just for performance, you MUST ensure your units are aligned before doing any calculations,
 *  and you MUST make sure the FdeCdfSpecification.defaultMeasurementUnit for your comparison metric aligns with the output units.
 */
// ----------------------------------------------------------------------------------------

export const THICKNESS_CDF_SIGNIFICANT_DIGITS = 3
// EMAT wastage thresholds based on condition categories
const THRESHOLD_MINOR_WASTAGE = -5 // 5% wastage threshold
const THRESHOLD_MODERATE_WASTAGE = -20 // 20% wastage threshold
const THRESHOLD_SEVERE_WASTAGE = -25 // 25% wastage threshold

/**
 * Calculate values derived from the thickness values.  What values will be calculated is determined by the `calculateBinValue` function,
 * which should be prepped in the caller.
 * @param parentComponentId
 * @param thicknessValues           - a 2D array of the raw thickness values, from which the derived values will be calculated.
 *                                    While all values are classified as numbers, they may be `NaN`.
 * @param thicknessUnit             - the unit of the thickness values
 * @param binHeight                 - the height of each bin
 * @param binWidth                  - the width of each bin
 * @param yGeometryStart            - the y start offset (gotten from cdf.data.geometry.y.start)
 * @param xGeometryStart            - the x start offset (gotten from cdf.data.geometry.x.start)
 * @param calculateBinValue         - a function that calculates the value (i.e. loss, rl, or tmin) for a given bin,
 *                                    given the x and y coordinates for the bin in feet
 * @param isOverrideNaN             - should NaN values be overridden with the calculated value?
 * @param roundToSignificantFigures - should the derived values be rounded to the number of significant figures in the
 *                                    Thickness CDF (3)
 */

export const calculateDerivedValues = (
  parentComponentId: string,
  thicknessValues: ReadonlyArray<ReadonlyArray<number>>,
  thicknessUnit: GeckoMeasurementUnit,
  binHeight: GeckoMeasurement,
  binWidth: GeckoMeasurement,
  yGeometryStart: GeckoMeasurement,
  xGeometryStart: GeckoMeasurement,
  calculateBinValue: CalculateBinValueFunction,
  isOverrideNaN: boolean = false,
  roundToSignificantFigures: boolean = true
) =>
  calculateDerivedValuesWithMetadata<undefined>(
    parentComponentId,
    thicknessValues,
    thicknessUnit,
    binHeight,
    binWidth,
    yGeometryStart,
    xGeometryStart,
    calculateBinValue,
    isOverrideNaN,
    roundToSignificantFigures
  )

export interface BinData {
  binValue: number
}

type DERIVED_VALUE<METADATA> = METADATA extends undefined
  ? number
  : (METADATA & BinData) | number

export function calculateDerivedValuesWithMetadata<
  METADATA extends object | undefined = undefined,
>(
  parentComponentId: string,
  thicknessValues: ReadonlyArray<ReadonlyArray<number>>,
  thicknessUnit: GeckoMeasurementUnit,
  binHeight: GeckoMeasurement,
  binWidth: GeckoMeasurement,
  yGeometryStart: GeckoMeasurement,
  xGeometryStart: GeckoMeasurement,
  calculateBinValue: CalculateBinValueFunction<METADATA>,
  isOverrideNaN: boolean = false,
  roundToSignificantFigures: boolean = true
): DERIVED_VALUE<METADATA>[][] {
  const binValues = new Array<Array<DERIVED_VALUE<METADATA>>>(
    thicknessValues.length
  )

  const halfBinHeight = new Decimal(binHeight.num).div(2)
  const halfBinWidth = new Decimal(binWidth.num).div(2)

  //in the very improbable case where there is a discrepancy between the y/xGeometryStart and the binHeight/Width units convert
  //everything to the binHeight/Width units to ensure consistency and convert to metric after running the math
  const yGeometryStartConsistent = yGeometryStart.convertTo(binHeight.unit)
  const xGeometryStartConsistent = xGeometryStart.convertTo(binWidth.unit)

  // this is defined out here so only 1 bin-rect object is ever stored in memory at a time
  let activeBinRect: GeckoComponentRect | undefined

  for (let y = 0; y < thicknessValues.length; y += 1) {
    //convert from number back into bin height units to let the conversion handle units correclty
    const yPosition = new Decimal(binHeight.num)
      .mul(y)
      .plus(yGeometryStartConsistent.num)
      .plus(halfBinHeight)

    binValues[y] = new Array<DERIVED_VALUE<METADATA>>(thicknessValues[y].length)

    for (let x = 0; x < thicknessValues[y].length; x += 1) {
      const binThicknessRaw = thicknessValues[y][x]
      if (!isOverrideNaN && isNaN(binThicknessRaw)) {
        binValues[y][x] = binThicknessRaw as DERIVED_VALUE<METADATA>
        continue
      }

      const xPosition = new Decimal(binWidth.num)
        .mul(x)
        .plus(xGeometryStartConsistent.num)
        .plus(halfBinWidth)

      activeBinRect = GeckoComponentRect.CreateFromXYs(
        parentComponentId,
        xPosition.minus(halfBinWidth).toNumber(),
        yPosition.minus(halfBinHeight).toNumber(),
        xPosition.plus(halfBinWidth).toNumber(),
        yPosition.plus(halfBinHeight).toNumber(),
        binWidth.unit,
        binHeight.unit
      ).convertTo(GeckoMeasurementUnit.Meters, GeckoMeasurementUnit.Meters)

      const measurement = calculateBinValue(
        activeBinRect,
        GeckoMeasurement.CreateFromValue(
          binThicknessRaw,
          //NOTE: This is not guaranteed to be in metric, the `calculateBinValue` function should convert to metric.
          //      We don't convert to metric here because not all `calculateBinValue` functions use value
          thicknessUnit
        ),
        { x, y }
      )
      if (measurement instanceof GeckoMeasurement) {
        binValues[y][x] = (
          roundToSignificantFigures
            ? measurement.toPrecision(THICKNESS_CDF_SIGNIFICANT_DIGITS)
            : measurement
        ).num as DERIVED_VALUE<METADATA>
      } else {
        binValues[y][x] = {
          ...(measurement as object),
          binValue: (roundToSignificantFigures
            ? measurement.binValue.toPrecision(THICKNESS_CDF_SIGNIFICANT_DIGITS)
            : measurement.binValue
          ).num,
        } as DERIVED_VALUE<METADATA>
      }
    }
  }
  return binValues
}

// ============================================ Derived Value Functions ============================================
// These functions organize data, and call `calculateDerivedValues` with the appropriate `calculateBinValue` function.

// -------------------------------------------- Generic Helpers --------------------------------------------

/**
 * Get the shell-course number for a given coordinate
 *
 * Note: The shell courses should be sorted in ascending order before calling this function
 */
export function getShellCourseNumForBinRect(
  shellCoursesInAscendingOrder: readonly GeckoShellCourse[],
  binRect: GeckoComponentRect
): number {
  if (!shellCoursesInAscendingOrder.length) {
    // we still use this for the custom CR/tmin functions on non-tanks, so we need to return a value
    return 1
  }
  const shellCourseIndex = shellCoursesInAscendingOrder.findLastIndex((sc) =>
    GeckoComponentRect.Overlaps(sc.geometry, binRect)
  )
  if (shellCourseIndex === -1) {
    console.warn(
      'Could not find shell course for bin',
      binRect,
      shellCoursesInAscendingOrder
    )
    return shellCoursesInAscendingOrder.length
  }
  return shellCourseIndex + 1
}

// -------------------------------------------- Line Averaged Thickness --------------------------------------------

export function getLineAveragedThicknessBinValueFunction(props: {
  forecastComponentConfig: ForecastComponentConfig
  thicknessValues: ReadonlyArray<ReadonlyArray<number>>
  assetModel: GeckoAssetModel
  componentId: string
  xGeometryStart: GeckoMeasurement
  yGeometryStart: GeckoMeasurement
  inspectionDate: dayjs.Dayjs
  binWidth: GeckoMeasurement
  binHeight: GeckoMeasurement
  tminMethod: GeckoTMinMethod
  componentType: GeckoComponentModelType
}): CalculateBinValueFunction<{
  lineCoordinates?: { start: number; end: number }
}> {
  const { thicknessValues, assetModel, componentId } = props

  const componentModel = assetModel.data.components.get(componentId)
  if (!componentModel) {
    throw new Error(`Component with id ${componentId} not found in asset model`)
  }
  const diameter = getComponentRadius(componentModel)?.scale(2)

  if (isNil(diameter)) {
    throw new Error(
      `Missing diameter, cannot calculate line averaged thickness: ${JSON.stringify(componentModel)}`
    )
  }
  const diameterFeet = diameter.convertTo(GeckoMeasurementUnit.Feet).num

  const yLen = thicknessValues.length
  return function calculateBinLineAveragedThickness(
    activeBinRect: GeckoComponentRect,
    thickness: GeckoMeasurement,
    indices?: { x: number; y: number }
  ) {
    if (!indices || isNaN(thickness.num)) {
      return { binValue: thickness }
    }
    const { x, y } = indices
    const inThickness = thickness.convertTo(GeckoMeasurementUnit.Inches).num
    const lineLength = Math.min(40, 3.7 * Math.sqrt(inThickness * diameterFeet))
    const binsToSpan = Math.floor(lineLength / 3)

    const averages: { binValue: number; windowStart: number }[] = []
    // find the first element that is binsToSpan away from current element or less
    // to start sliding window
    const windowStart = Math.max(0, y - binsToSpan + 1)
    // find the last element that is binsToSpan away from current element while also less than yLen
    // to end sliding window
    // windowLastStart is the last element that can be the start of the window
    // the window always needs to be binsToSpan long
    const windowLastStart = Math.min(y, yLen - binsToSpan)
    // ex. y = 7, binsToSpan = 14, yLen = 120
    // windowStart = 0, windowLastStart = 7
    // ex. y = 35, binsToSpan = 14, yLen = 120
    // windowStart = 22, windowLastStart = 35
    // ex. y = 117, binsToSpan = 5, yLen = 120
    // windowStart = 112, windowLastStart = 115
    // iterate through all possible windows
    for (let i = windowStart; i <= windowLastStart; i += 1) {
      const windowThicknesses = []
      for (let j = 0; j < binsToSpan; j += 1) {
        // if not NaN, add to windowThicknesses
        if (!isNaN(thicknessValues[i + j][x])) {
          windowThicknesses.push(thicknessValues[i + j][x])
        }
      }
      if (windowThicknesses.length > 0) {
        averages.push({
          binValue: sum(windowThicknesses) / windowThicknesses.length,
          windowStart: i,
        })
      }
    }
    const averageMin = averages.reduce((minAverage, average) => {
      return minAverage.binValue < average.binValue ? minAverage : average
    }, averages[0])

    return {
      binValue: GeckoMeasurement.CreateFromValue(
        averages.length >= 2 && averageMin.binValue > inThickness
          ? averageMin.binValue
          : inThickness,
        GeckoMeasurementUnit.Inches
      ),
      lineCoordinates: {
        start: averageMin.windowStart,
        end: averageMin.windowStart + binsToSpan,
      },
    }
  }
}

// -------------------------------------------- Bin Loss --------------------------------------------

/**
 * Calculates the Loss for a bin, this loss bin-value-function can take in a pre-calculated nominal for
 * performance optimizations (i.e. we calculate nominal for loss-per-year, we can just pass that nominal in here instead
 * of having to calculate it again
 */
export type CalculateLossBinValueFunction = (
  coordinates: GeckoComponentRect,
  thickness: GeckoMeasurement,
  indices?: { x: number; y: number },
  preCalculatedNominal?: GeckoMeasurement
) => GeckoMeasurement

export function getLossBinValueFunction(props: {
  assetModel: GeckoAssetModel
  forecastComponentConfig: ForecastComponentConfig | undefined
  inspectionDate: dayjs.Dayjs
}): CalculateLossBinValueFunction {
  const { assetModel, forecastComponentConfig, inspectionDate } = props

  return function calculateBinLoss(
    activeBinRect: GeckoComponentRect,
    thickness: GeckoMeasurement,
    indices?: { x: number; y: number },
    preCalculatedNominal?: GeckoMeasurement
  ) {
    const nominal =
      preCalculatedNominal ??
      getManufactureSpecsForBin({
        binBoundsToGetSpecsFor: activeBinRect,
        assetModel,
        forecastComponentConfig,
        latestDateToUse: inspectionDate?.toDate(),
      }).nominalThickness

    if (isNil(nominal)) {
      return GeckoMeasurement.CreateFromValue(
        Number.NaN,
        GeckoMeasurementUnit.Millimeters
      )
    }

    const loss = new Decimal(
      nominal.convertTo(GeckoMeasurementUnit.Millimeters).num
    )
      .minus(thickness.convertTo(GeckoMeasurementUnit.Millimeters).num)
      .toNumber()

    return GeckoMeasurement.CreateFromValue(
      loss,
      GeckoMeasurementUnit.Millimeters
    )
  }
}

// -------------------------------------------- Bin Loss By Percent --------------------------------------------

export function getLossByPercentBinValueFunction(props: {
  assetModel: GeckoAssetModel
  forecastComponentConfig: ForecastComponentConfig | undefined
  inspectionDate: dayjs.Dayjs
}): CalculateBinValueFunction {
  const { assetModel, forecastComponentConfig, inspectionDate } = props

  const lossBinValueFunction = getLossBinValueFunction({
    assetModel,
    forecastComponentConfig,
    inspectionDate,
  })

  return function calculateBinLossByPercent(
    binRect: GeckoComponentRect,
    thickness: GeckoMeasurement
  ) {
    const { nominalThickness } = getManufactureSpecsForBin({
      binBoundsToGetSpecsFor: binRect,
      assetModel,
      forecastComponentConfig,
      latestDateToUse: inspectionDate?.toDate(),
    })

    if (isNil(nominalThickness)) {
      return GeckoMeasurement.CreateFromValue(
        Number.NaN,
        GeckoMeasurementUnit.Percent
      )
    }

    const loss = lossBinValueFunction(
      binRect,
      thickness,
      undefined,
      nominalThickness
    )

    return GeckoMeasurement.CreateFromValue(
      new Decimal(loss.convertTo(GeckoMeasurementUnit.Millimeters).num)
        .div(nominalThickness.convertTo(GeckoMeasurementUnit.Millimeters).num)
        .mul(-100) // negative because a if thickness is 7 and nominal is 10, the loss should display as -30%, and 100 to convert to percentage
        .toNumber(),
      GeckoMeasurementUnit.Percent
    )
  }
}

// -------------------------------------------- CR By ShellCourse Fn --------------------------------------------

export type FindCustomCrForShellCourseFn = (
  activeBinRect: GeckoComponentRect
) => GeckoMeasurement

export function getFindCustomCrForShellCourseFn(
  componentRam: GeckoComponentModel,
  forecastComponentConfig: ForecastComponentConfig
): FindCustomCrForShellCourseFn {
  const { cr_by_shell_course } = forecastComponentConfig
  if (isNil(cr_by_shell_course)) {
    throw new Error('Custom CR is enabled, but no CR data is available.')
  }
  const shellCourses =
    componentRam.type === GeckoComponentModelType.VerticalTankShell
      ? componentRam.coursesArray
      : []

  const shellCoursesInAscendingOrder = shellCourses.toSorted(
    (a, b) =>
      a.geometry.y.end
        .minus(b.geometry.y.end)
        .convertTo(GeckoMeasurementUnit.Meters).num
  )

  return (binRect) => {
    const shellCourseElevation = getShellCourseNumForBinRect(
      shellCoursesInAscendingOrder,
      binRect
    )

    return GeckoMeasurement.CreateFromValue(
      cr_by_shell_course[shellCourseElevation],
      GeckoMeasurementUnit.InchesPerYear
    )
  }
}

// -------------------------------------------- Bin Loss Per Year --------------------------------------------

function mergeIntervals({
  coatingRegions,
  inspectionDate,
  binInstallDate,
}: {
  coatingRegions: (GeckoCoatingRegion | GeckoLinerRegion)[]
  inspectionDate: Date
  binInstallDate?: Date
}): (GeckoCoatingRegion | GeckoLinerRegion)[] {
  if (coatingRegions.length === 0) return []

  //filterout intervals that don't have a failure date
  //only handle the failure date case atm
  const validCoatingRegions = coatingRegions.filter((region) => {
    //bit of logic here - if the failure date is null, we can't use this region
    //if the installation date is null and the binInstall Date is null, we can't use this region
    // if the failure date is before the installation date, we can't use this region
    // if the failure date is after the inspection date we can't use this region

    if (isNil(region.failureDate)) {
      return false
    }
    const startDate = region?.installationDate ?? binInstallDate
    if (isNil(startDate)) {
      return false
    }
    if (region.failureDate < startDate || region.failureDate > inspectionDate) {
      return false
    }
    return true
  })

  if (validCoatingRegions.length === 0) {
    return []
  }

  // Sort intervals by start date
  const validSortedCoatingRegions = validCoatingRegions.sort((a, b) => {
    //as cast safe here since we're checking above
    const aStartDate = (a?.installationDate ?? binInstallDate) as Date
    const bStartDate = (b?.installationDate ?? binInstallDate) as Date
    return aStartDate.getTime() - bStartDate.getTime()
  })

  const mergedIntervals: (GeckoCoatingRegion | GeckoLinerRegion)[] = []
  let currentInterval = validSortedCoatingRegions[0]

  for (let i = 1; i < validSortedCoatingRegions.length; i++) {
    const interval = coatingRegions[i]
    const currentIntervalEnd = dayjs(currentInterval.failureDate)

    const intervalEnd = dayjs(interval.failureDate)
    const intervalStart = dayjs(interval?.installationDate ?? binInstallDate)

    // Check if the current interval overlaps with the next interval
    if (
      currentIntervalEnd.isAfter(intervalStart) ||
      currentIntervalEnd.isSame(intervalStart)
    ) {
      // Merge the intervals by updating the end date of the current interval
      currentInterval.failureDate = dayjs
        .max(currentIntervalEnd, intervalEnd)
        .toDate()
    } else {
      // No overlap, add the current interval to the merged intervals and move to the next interval
      mergedIntervals.push(currentInterval)
      currentInterval = interval
    }
  }

  // Add the last interval
  mergedIntervals.push(currentInterval)

  return mergedIntervals
}

export function getLossPerYearBinValueFunction(props: {
  inspectionDate: dayjs.Dayjs
  corrosionAugmentationRegions: CustomCorrosionRegion[]
  findCustomCrForShellCourseFn?: FindCustomCrForShellCourseFn
  assetModel: GeckoAssetModel
  calculateRcaBinValueFn?: CalculateBinValueFunction | null
  centerPlateRadius?: number
  forecastComponentConfig: ForecastComponentConfig | undefined
  historicalCorrosionArrestRegions?: (GeckoCoatingRegion | GeckoLinerRegion)[]
}): CalculateBinValueFunction {
  const {
    inspectionDate,
    corrosionAugmentationRegions,
    findCustomCrForShellCourseFn,
    assetModel,
    calculateRcaBinValueFn = null,
    forecastComponentConfig,
    historicalCorrosionArrestRegions,
  } = props

  if (findCustomCrForShellCourseFn) {
    return function customBinLossPerYear(
      binRect: GeckoComponentRect,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
      thickness: GeckoMeasurement
    ) {
      const customCr = findCustomCrForShellCourseFn(binRect)
      return customCr.convertTo(GeckoMeasurementUnit.MillimetersPerYear)
    }
  }

  const lossBinValueFunction = getLossBinValueFunction({
    assetModel,
    forecastComponentConfig,
    inspectionDate,
  })

  // these values are in ft because of the corrosionAugmentationRegions check
  return function calculateBinLossPerYear(
    binRect: GeckoComponentRect,
    thickness: GeckoMeasurement
  ) {
    const { installationDate: binInstallDate, nominalThickness } =
      getManufactureSpecsForBin({
        binBoundsToGetSpecsFor: binRect,
        assetModel,
        forecastComponentConfig,
        latestDateToUse: inspectionDate.toDate(),
      })

    if (isNil(binInstallDate) || isNil(nominalThickness)) {
      return GeckoMeasurement.CreateFromValue(
        Number.NaN,
        GeckoMeasurementUnit.MillimetersPerYear
      )
    }

    const daysElapsed = inspectionDate.diff(binInstallDate, 'd')

    const lossBinValue = lossBinValueFunction(
      binRect,
      thickness,
      undefined,
      nominalThickness
    )

    if (
      !isNil(historicalCorrosionArrestRegions) &&
      !isEmpty(historicalCorrosionArrestRegions)
    ) {
      //Case for historical coating Regions
      //The coating regions affect the time delta that the bin has been in service
      //We need to subtract the time delta of the coating regions from the days elapsed
      const binRectFeet = binRect.convertTo(
        GeckoMeasurementUnit.Feet,
        GeckoMeasurementUnit.Feet
      )
      //multiple coating regions could overlap at the same time or multiple times
      const overlappedCoatingRegions = historicalCorrosionArrestRegions.filter(
        (region) => GeckoComponentRect.Overlaps(region.geometry, binRectFeet)
      )
      if (!isEmpty(overlappedCoatingRegions)) {
        //if there are multiple coating regions we need to combine intervals that overlap and subtract all regions time deltas from the days elapsed
        const mergedCoatingRegions = mergeIntervals({
          coatingRegions: overlappedCoatingRegions,
          binInstallDate: binInstallDate,
          inspectionDate: inspectionDate.toDate(),
        })
        const totalTimeDeltaDays = mergedCoatingRegions.reduce(
          (total, region) => {
            const startDate = region.installationDate ?? binInstallDate
            return total + dayjs(region.failureDate).diff(startDate, 'd')
          },
          0
        )
        const daysElapsedWithCoatingTimeSubtracted =
          daysElapsed - totalTimeDeltaDays
        const yearsElapsedWithCoatingTimeSubtracted = new Decimal(
          daysElapsedWithCoatingTimeSubtracted
        ).div(365)

        return GeckoMeasurement.CreateFromValue(
          new Decimal(
            lossBinValue.convertTo(GeckoMeasurementUnit.Millimeters).num
          )
            .div(yearsElapsedWithCoatingTimeSubtracted)
            .toNumber(),
          GeckoMeasurementUnit.MillimetersPerYear
        )
      }
    }

    const yearsElapsed = new Decimal(daysElapsed).div(365)

    const lossBinPerYear = new Decimal(lossBinValue.num).div(yearsElapsed)

    // get rca if the function is provided
    const rca =
      calculateRcaBinValueFn !== null
        ? new Decimal(
            calculateRcaBinValueFn(binRect, thickness).convertTo(
              GeckoMeasurementUnit.Millimeters
            ).num
          )
        : null
    let corrosionMultiplier
    if (!isEmpty(corrosionAugmentationRegions)) {
      const binRectFeet = binRect.convertTo(
        GeckoMeasurementUnit.Feet,
        GeckoMeasurementUnit.Feet
      )
      for (const region of corrosionAugmentationRegions) {
        const result = calculateCorrosionMultiplier(
          region,
          binRectFeet,
          thickness,
          rca,
          lossBinPerYear
        )
        if (result.shouldReturn && result.value) {
          // if should return the result value is in millimeters
          return result.value
        } else if (result.multiplier) {
          corrosionMultiplier = result.multiplier
        }
      }
    }

    return GeckoMeasurement.CreateFromValue(
      lossBinPerYear.mul(corrosionMultiplier ?? 1).toNumber(),
      GeckoMeasurementUnit.MillimetersPerYear
    )
  }
}

// -------------------------------------------------- T_min --------------------------------------------------

export function getTminBinValueFunction(props: {
  tminMethod: GeckoTMinMethod
  weldLines: ReadonlyArray<GeckoVerticalWeld | GeckoHorizontalWeld>
  forecastComponentConfig: ForecastComponentConfig | undefined
  binHeight: GeckoMeasurement
  binWidth: GeckoMeasurement
  diameter: GeckoMeasurement | undefined
  componentType: GeckoComponentModelType | undefined
  componentId: string
  wrapRegions?: PipeWrapRegion[]
  componentBounds: GeckoComponentRect
  assetModel: GeckoAssetModel
}): CalculateBinValueFunction {
  const {
    tminMethod,
    weldLines,
    forecastComponentConfig,
    binHeight,
    binWidth,
    diameter,
    componentId,
    wrapRegions,
    componentBounds,
    assetModel,
  } = props
  const assetModelComponent = assetModel.data.components.get(componentId)

  if (tminMethod === GeckoTMinMethod.TMinByApi570) {
    const assetModelComponent = assetModel.data.components.get(componentId)

    const { allowable_unit_stress, operation_pressure, pipe_outer_diameter } =
      forecastComponentConfig ?? {}
    const joint_efficiency =
      assetModelComponent?.type === GeckoComponentModelType.PipeSection
        ? assetModelComponent.jointEfficiency
        : undefined

    if (
      isNil(allowable_unit_stress) ||
      isNil(operation_pressure) ||
      isNil(pipe_outer_diameter) ||
      isNil(joint_efficiency)
    ) {
      throw new Error(
        `Missing a required value for T_min by API 574 calculations - 
        allowable_unit_stress: ${allowable_unit_stress}, 
        operation_pressure: ${operation_pressure}, 
        pipe_outer_diameter: ${pipe_outer_diameter}, 
        joint_efficiency: ${joint_efficiency}`
      )
    }

    return getTminByApi570BinValueFunction({
      allowable_unit_stress: GeckoMeasurement.CreateFromValue(
        allowable_unit_stress,
        GeckoMeasurementUnit.PoundsForcePerSqInch
      ),
      operation_pressure: GeckoMeasurement.CreateFromValue(
        operation_pressure,
        GeckoMeasurementUnit.PoundsForcePerSqInch
      ),
      pipe_outer_diameter: GeckoMeasurement.CreateFromValue(
        pipe_outer_diameter,
        GeckoMeasurementUnit.Feet
      ),
      joint_efficiency: GeckoMeasurement.CreateFromValue(
        joint_efficiency,
        GeckoMeasurementUnit.None
      ),
      wrapRegions,
    })
  } else if (tminMethod === GeckoTMinMethod.TMinByApi653) {
    if (!(assetModelComponent instanceof GeckoVerticalTankShell)) {
      throw new Error(
        `TMinByApi653 is only valid for VerticalTankShell components`
      )
    }
    const { specific_gravity_profile } = forecastComponentConfig ?? {}

    const courses = assetModelComponent.coursesArray

    const upper_courses_stress_tolerance =
      assetModelComponent?.type === GeckoComponentModelType.VerticalTankShell
        ? assetModelComponent.stressToleranceUpperShellCourses?.convertTo(
            GeckoMeasurementUnit.PoundsForcePerSqInch
          )
        : undefined
    const lower_courses_stress_tolerance =
      assetModelComponent?.type === GeckoComponentModelType.VerticalTankShell
        ? assetModelComponent.stressToleranceLowerShellCourses?.convertTo(
            GeckoMeasurementUnit.PoundsForcePerSqInch
          )
        : undefined
    const joint_efficiency =
      assetModelComponent?.type === GeckoComponentModelType.VerticalTankShell
        ? assetModelComponent.jointEfficiency
        : undefined
    const specific_gravity =
      assetModel.data.type === GeckoAssetModelType.VerticalTank
        ? (assetModel.data.specificGravity as number)
        : undefined
    const engineered_fill_elevation =
      assetModel.data.type === GeckoAssetModelType.VerticalTank
        ? (assetModel.data.fillElevation as GeckoMeasurement)?.convertTo(
            GeckoMeasurementUnit.Feet
          )
        : undefined

    const isStressToleranceValid =
      //either we have both upper and lower courses stress tolerance or we have a stress tolerance for every course
      (!isNil(lower_courses_stress_tolerance) &&
        !isNil(upper_courses_stress_tolerance)) ||
      courses.every((sc) => !!sc.stressTolerance)

    if (
      //slightly more complicated check for tolerances since we could have every cours having a tolerance or an upper and lower course stress tolerance
      !isStressToleranceValid ||
      isNil(specific_gravity) ||
      isNil(diameter) ||
      isNil(joint_efficiency) ||
      isNil(engineered_fill_elevation)
    ) {
      throw new Error(`Missing a required value for T_min by API 653 calculations -
        lower_courses_stress_tolerance: ${lower_courses_stress_tolerance},
        upper_courses_stress_tolerance: ${upper_courses_stress_tolerance},
        specific_gravity: ${specific_gravity},
        diameter: ${diameter},
        joint_efficiency: ${joint_efficiency},
        engineered_fill_elevation: ${engineered_fill_elevation}`)
    }

    return getTminByApi653BinValueFunction({
      shellCourses: assetModelComponent.coursesArray,
      binHeight,
      binWidth,
      weldLines,
      lowerCoursesStressTolerance: lower_courses_stress_tolerance,
      upperCoursesStressTolerance: upper_courses_stress_tolerance,
      specificGravity: GeckoMeasurement.CreateFromValue(
        specific_gravity,
        GeckoMeasurementUnit.None
      ),
      specificGravityProfile: specific_gravity_profile,
      componentDiameter: diameter,
      jointEfficiency: GeckoMeasurement.CreateFromValue(
        joint_efficiency,
        GeckoMeasurementUnit.None
      ),
      fillHeight: engineered_fill_elevation,
      componentBounds,
    })
  } else if (tminMethod === GeckoTMinMethod.TMinByShellCourse) {
    if (
      !(
        assetModelComponent instanceof GeckoVerticalTankShell ||
        assetModelComponent instanceof GeckoLegacyCone
      )
    ) {
      throw new Error(
        `TMinByShellCourse is only valid for VerticalTankShell components`
      )
    }

    const courses = assetModelComponent.coursesArray

    if (!courses.every((sc) => !!sc.tmin)) {
      throw new Error(
        'Missing a required value for T_min by shell course calculations - shell courses'
      )
    }

    return getTminBySectionBinValueFunction(courses)
  } else if (tminMethod.id === GeckoTMinMethod.TMinWithAnnularRing.id) {
    if (isNil(forecastComponentConfig)) {
      throw new Error(
        `Missing forecastComponentConfig, cannot piecewise tmin for annular ring: ${JSON.stringify(
          assetModelComponent
        )}`
      )
    }
    return getTminByAnnularRingFloorsFunction(
      forecastComponentConfig,
      diameter?.scale(0.5)
    )
  } else if (tminMethod.id === GeckoTMinMethod.TMinByComponent.id) {
    // this is a custom tmin function that is used for components that have a single tmin value
    // Currently used for floors and roofs

    const componentModel = assetModel.data.components.get(componentId)
    if (
      !componentModel ||
      (!(componentModel instanceof GeckoTankFlatRoof) &&
        !(componentModel instanceof GeckoTankFloor) &&
        !(componentModel instanceof GeckoHorizontalTankShell) &&
        !(componentModel instanceof GeckoPipeSection)) ||
      isNil(componentModel.tmin)
    ) {
      throw new Error(
        `Component with id ${componentId} not found in asset model`
      )
    }
    const tmin = componentModel.tmin

    return function calculateBinTminByComponent(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
      binRect: GeckoComponentRect,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
      thickness: GeckoMeasurement
    ) {
      return tmin.convertTo(GeckoMeasurementUnit.Millimeters)
    }
  } else if (tminMethod.id === GeckoTMinMethod.TMinByWall.id) {
    if (!(assetModelComponent instanceof GeckoLegacyBoilerWalls)) {
      throw new Error(
        `TMinByShellCourse is only valid for VerticalTankShell components`
      )
    }

    const walls = assetModelComponent.wallsArray

    if (!walls.every((walls) => !!walls.tmin)) {
      throw new Error(
        'Missing a required value for T_min by shell course calculations - shell courses'
      )
    }

    return getTminBySectionBinValueFunction(walls)
  }
  throw new Error(`Invalid tminMethod: ${tminMethod.id}`)
}

// - - - - - - - - - - - - - - - - - - - - - - - Piecewise T_min - - - - - - - - - - - - - - - - - - - - - - -
// Formerly known as 'tmin by shell-course', this function applies a piecewise function to calculate t_min currently supports Vertical Tank Shells
// Cones, and Legacy Boilers

function getTminBySectionBinValueFunction(
  sections: ReadonlyArray<
    GeckoShellCourse | GeckoLegacyBoilerWall | GeckoConeCourse
  >
): CalculateBinValueFunction {
  return function calculateBinTminBySection(
    binRect: GeckoComponentRect,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    thickness: GeckoMeasurement
  ) {
    const sectionOverlap = sections.findLast((d) => {
      //Check the last section that overlaps with the bin - mainly for intersecting bins so we use the upper course in tanks and the right wall in boilers
      return GeckoComponentRect.Overlaps(d.geometry, binRect)
    })
    const tmin =
      sectionOverlap?.tmin ??
      GeckoMeasurement.CreateFromValue(NaN, GeckoMeasurementUnit.Meters)

    return tmin.convertTo(GeckoMeasurementUnit.Millimeters)
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - Piecewise T_min - - - - - - - - - - - - - - - - - - - - - - -
// This function returns a tmin based on whether the bin is withing the center plates or in the annular ring of a tank bottom

function getTminByAnnularRingFloorsFunction(
  forecastComponentConfig: ForecastComponentConfig,
  radius: GeckoMeasurement | undefined
): CalculateBinValueFunction {
  const {
    center_plate_radius: centerPlateRadius,
    tmin_by_shell_course: tminByShellCourse,
  } = forecastComponentConfig

  if (isNil(tminByShellCourse) || isNil(radius) || isNil(centerPlateRadius)) {
    throw new Error(
      `Missing tmin by shell course, cannot calculate piecewise tmin: ${JSON.stringify(forecastComponentConfig)}`
    )
  }

  const floorRadius = radius.convertTo(GeckoMeasurementUnit.Meters)

  // assume the origin is aligned to the left and bottom of the asset
  const annularRingStartRadius = floorRadius

  return function calculateBinTminByShellCourse(
    binRect: GeckoComponentRect,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    thickness: GeckoMeasurement
  ) {
    const isBinInAnnularRing = isInAnnularRing(
      binRect,
      floorRadius,
      annularRingStartRadius
    )
    const tmin = tminByShellCourse[isBinInAnnularRing ? 2 : 1]

    return GeckoMeasurement.CreateFromValue(
      tmin,
      GeckoMeasurementUnit.Inches
    ).convertTo(GeckoMeasurementUnit.Millimeters)
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - Tmin by API 653 - - - - - - - - - - - - - - - - - - - - - - - -
export const DEFAULT_JOINT_EFFICIENCY_BUFFER_FT = 0.5

/**
 * Get all the areas (rects) where joint efficiency should be applied in API-653 calculations from a set of weld lines
 *
 */
export function getJointEfficiencyBufferRectsFromWeldLines(
  weldLines: ReadonlyArray<GeckoVerticalWeld | GeckoHorizontalWeld>,
  componentGeometry: GeckoComponentRect,
  wrapsHorizontally: boolean
) {
  const jointEfficiencyBufferHeight = GeckoMeasurement.CreateFromValue(
    DEFAULT_JOINT_EFFICIENCY_BUFFER_FT,
    GeckoMeasurementUnit.Feet
  )

  const jointEfficiencyBufferWidth = GeckoMeasurement.CreateFromValue(
    DEFAULT_JOINT_EFFICIENCY_BUFFER_FT,
    GeckoMeasurementUnit.Feet
  )
  const componentCircumference = componentGeometry.x.delta

  return weldLines.map((wL) => {
    const geometry = wL.feature_geometry
    const componentId = geometry.componentID

    // calculate the new x start and end points and deal with the wrap-around (API-653 is only on tanks)
    const newXStartTemp = geometry.x.start.minus(jointEfficiencyBufferWidth)
    const newXStart =
      wrapsHorizontally && newXStartTemp.lt(componentGeometry.x.start)
        ? componentCircumference.minus(
            componentGeometry.x.start.minus(newXStartTemp)
          )
        : newXStartTemp

    const newXEndTemp = geometry.x.end.plus(jointEfficiencyBufferWidth)
    const newXEnd =
      wrapsHorizontally && newXEndTemp.gt(componentCircumference)
        ? newXEndTemp.minus(componentCircumference)
        : newXEndTemp

    // if x-start wraps around but is still smaller than x-end, or x-end wraps around but is still larger than x-start,
    //  then we have a weld line that wraps around the whole tank and should just use the full tank circumference
    if (
      // handle start wrap error
      (newXStart.gt(newXStartTemp) && // we wrapped
        newXStart.lt(newXEndTemp)) || // we are smaller than the end + buffer (not wrapped)
      // handle end wrap error
      (newXEnd.lt(newXEndTemp) && // we wrapped
        newXEnd.gt(newXStartTemp)) // we are still larger than the start - buffer (not wrapped)
    ) {
      return GeckoComponentRect.CreateFromCoords(
        GeckoComponentCoords.CreateFromMeasurements(
          componentId,
          componentGeometry.x.start,
          geometry.y.start.minus(jointEfficiencyBufferHeight)
        ),
        GeckoComponentCoords.CreateFromMeasurements(
          componentId,
          componentGeometry.x.end,
          geometry.y.end.plus(jointEfficiencyBufferHeight)
        )
      )
    }
    // the weld line extended to include the joint efficiency buffer
    return GeckoComponentRect.CreateFromCoords(
      GeckoComponentCoords.CreateFromMeasurements(
        componentId,
        newXStart,
        geometry.y.start.minus(jointEfficiencyBufferHeight)
      ),
      GeckoComponentCoords.CreateFromMeasurements(
        componentId,
        newXEnd,
        geometry.y.end.plus(jointEfficiencyBufferHeight)
      )
    )
  })
}

/**
 * A function to get the joint efficiency for a given bin -- this is all in feet because we use the imperial formula for
 * API-653.
 *
 * Note: the joint-efficiency value returned is unit-less, as it is a ratio
 */

const DEFAULT_JOINT_EFFICIENCY = GeckoMeasurement.CreateFromValue(
  1,
  GeckoMeasurementUnit.None
)

/**
 * Get the joint efficiency for a given coordinate
 *
 * @param binRect               The a rectangle describing the bounds of a bin
 * @param weldLineBufferRects   The areas where joint efficiency should be applied, use the
 *                              `getJointEfficiencyBufferRectsFromWeldLines` function to get this value
 * @param jointEfficiency       The joint efficiency to apply if `coordinates` is in a `weldLineBufferRects`
 */
export function getJointEfficiency(
  binRect: GeckoComponentRect,
  weldLineBufferRects: ReadonlyArray<GeckoComponentRect>,
  jointEfficiency: GeckoMeasurement
): GeckoMeasurement {
  const isWithinWeldBoundary = weldLineBufferRects.some((rect) =>
    GeckoComponentRect.Overlaps(rect, binRect)
  )

  return isWithinWeldBoundary ? jointEfficiency : DEFAULT_JOINT_EFFICIENCY
}

type TminByApi653Props = {
  shellCourses: ReadonlyArray<GeckoShellCourse>
  binHeight: GeckoMeasurement
  binWidth: GeckoMeasurement
  weldLines: ReadonlyArray<GeckoVerticalWeld | GeckoHorizontalWeld>
  lowerCoursesStressTolerance?: GeckoMeasurement
  upperCoursesStressTolerance?: GeckoMeasurement
  specificGravity: GeckoMeasurement
  specificGravityProfile?: SpecificGravityProfileEnum
  componentDiameter: GeckoMeasurement
  jointEfficiency: GeckoMeasurement
  fillHeight: GeckoMeasurement
  componentBounds: GeckoComponentRect
}

function getTminByApi653BinValueFunction(
  props: TminByApi653Props
): CalculateBinValueFunction {
  const {
    binHeight,
    weldLines,
    shellCourses,
    lowerCoursesStressTolerance,
    upperCoursesStressTolerance,
    specificGravity,
    specificGravityProfile,
    componentDiameter,
    jointEfficiency,
    fillHeight,
    componentBounds,
  } = props

  const shellCoursesInAscendingOrder = shellCourses.toSorted(
    (a, b) =>
      a.geometry.y.end
        .minus(b.geometry.y.end)
        .convertTo(GeckoMeasurementUnit.Meters).num
  )

  const weldLineBufferRects = getJointEfficiencyBufferRectsFromWeldLines(
    weldLines,
    componentBounds,
    true // API-653 is only for vertical tank shells
  )

  const fillHeightFt = fillHeight.convertTo(GeckoMeasurementUnit.Feet)
  const componentDiameterFt = componentDiameter.convertTo(
    GeckoMeasurementUnit.Feet
  )

  const halfBinHeightFt = binHeight
    .scale(0.5)
    .convertTo(GeckoMeasurementUnit.Feet)

  return function calculateBinTminByApi653(
    binRect: GeckoComponentRect,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    thickness: GeckoMeasurement
  ) {
    // This function does its calculations in imperial units because the API 653 values are stored in imperial units,
    // and so we can use the formula most people are familiar with.  We may want to swap over to the metric formula
    // at some point

    // it also uses the stored coordinates for the bin (mid-point), this should also be changed to use the bottom (most aggressive)

    const yBinCenter = new Decimal(
      binRect.start.y.convertTo(GeckoMeasurementUnit.Feet).num
    )
      .plus(halfBinHeightFt.num)
      .toNumber()

    const yBinCenterMeasurementUnit = GeckoMeasurement.CreateFromValue(
      yBinCenter,
      GeckoMeasurementUnit.Feet
    )

    // calculate hydrostatic head
    const hydrostaticHead = new Decimal(fillHeightFt.num).minus(yBinCenter)

    // Stress Tolerance
    const stressToleranceForCoor = getStressToleranceForBinRect(
      binRect,
      shellCoursesInAscendingOrder,
      lowerCoursesStressTolerance,
      upperCoursesStressTolerance
    ).convertTo(GeckoMeasurementUnit.PoundsForcePerSqInch)

    const jointEfficiencyForElevation = getJointEfficiency(
      binRect,
      weldLineBufferRects,
      jointEfficiency
    )

    const gravityAtHeight = interpolateGravity(
      specificGravityProfile,
      yBinCenterMeasurementUnit,
      specificGravity.num,
      fillHeight
    ) as number

    const tmin = new Decimal(2.6)
      .mul(hydrostaticHead)
      .mul(componentDiameterFt.num)
      .mul(gravityAtHeight)
      .div(stressToleranceForCoor.num)
      .div(jointEfficiencyForElevation.num)
      .toNumber()

    return GeckoMeasurement.CreateFromValue(
      tmin < 0.1 ? 0.1 : tmin,
      GeckoMeasurementUnit.Inches
    ).convertTo(GeckoMeasurementUnit.Millimeters)
  }
}

export function getStressToleranceForBinRect(
  binRect: GeckoComponentRect,
  shellCourses: ReadonlyArray<GeckoShellCourse>,
  lowerCoursesStressTolerance?: GeckoMeasurement,
  upperCoursesStressTolerance?: GeckoMeasurement
): GeckoMeasurement {
  const shellCourseNumber = getShellCourseNumForBinRect(shellCourses, binRect)
  const shellCourse = shellCourses[shellCourseNumber - 1]
  if (shellCourse.stressTolerance) {
    return shellCourse.stressTolerance.convertTo(
      GeckoMeasurementUnit.PoundsForcePerSqInch
    )
  }
  return shellCourseNumber <= 2
    ? //non-null assertion is safe here since the caller is confirming that either all courses have a stress tolerance or
      // the upper and lower courses are set
      lowerCoursesStressTolerance!
    : upperCoursesStressTolerance!
}

// ------------------------------------------- Remaining Corrosion Allowance -------------------------------------------

export function getRcaBinValueFunction(props: {
  binWidth: GeckoMeasurement
  binHeight: GeckoMeasurement
  tminMethod: GeckoTMinMethod
  weldLines: ReadonlyArray<GeckoVerticalWeld | GeckoHorizontalWeld>
  shellCourses: ReadonlyArray<GeckoShellCourse>
  forecastComponentConfig: ForecastComponentConfig
  inspectionDate: dayjs.Dayjs
  assetModel: GeckoAssetModel
  componentId: string
  componentType: GeckoComponentModelType | undefined
  repairsToSimInstall: OverrideRegion[]
  percentageRCA?: boolean
}): CalculateBinValueFunction {
  const {
    binWidth,
    binHeight,
    tminMethod,
    weldLines,
    forecastComponentConfig,
    inspectionDate,
    assetModel,
    componentId,
    componentType,
    repairsToSimInstall = [],
    percentageRCA,
  } = props

  const componentRam = assetModel.data.components.get(componentId)

  if (!componentRam)
    throw new Error(`No component with id ${componentId} found in the Ram`)

  const diameter = getComponentRadius(componentRam)?.scale(2)

  const repairRegions =
    componentRam.type === GeckoComponentModelType.LegacyBoilerWalls
      ? componentRam.repairRegionsArray
      : componentRam.type === GeckoComponentModelType.VerticalTankShell ||
          componentRam.type === GeckoComponentModelType.TankFlatRoof ||
          componentRam.type === GeckoComponentModelType.TankFloor
        ? componentRam.platesArray
        : []

  const calculateBinTminFn = getTminBinValueFunction({
    tminMethod,
    weldLines,
    forecastComponentConfig,
    binHeight,
    binWidth,
    diameter,
    componentType,
    componentId,
    componentBounds: componentRam.componentBounds,
    assetModel,
  })

  //If there are any repair regions that are applied after the repair regions - include them in the repair regions
  //We pass these regions in here so that its used to override the thickness to be applied after the loss calculation
  const configRepairRegions = repairRegions
    .filter((d) => {
      return (
        dayjs(d.installationDate).isAfter(inspectionDate) ||
        dayjs(d.installationDate).isSame(inspectionDate)
      )
    })
    // map to override regions for now, so they can be merged with `repairsToSimInstall`
    .map((repair): OverrideRegion => {
      const isNominalAdditive =
        isNil(repair.wallNominalThickness) && !!repair.coatingNominalThickness
      // the converts in this return will return tubes automatically if the base unit is tubes
      return {
        end_x: repair.geometry.end.x.convertTo(GeckoMeasurementUnit.Feet).num,
        end_y: repair.geometry.end.y.convertTo(GeckoMeasurementUnit.Feet).num,
        start_x: repair.geometry.start.x.convertTo(GeckoMeasurementUnit.Feet)
          .num,
        start_y: repair.geometry.start.y.convertTo(GeckoMeasurementUnit.Feet)
          .num,
        nominal_thickness: isNominalAdditive
          ? repair.coatingNominalThickness?.convertTo(
              GeckoMeasurementUnit.Inches
            ).num
          : repair.wallNominalThickness?.convertTo(GeckoMeasurementUnit.Inches)
              .num,
        is_nominal_additive: isNominalAdditive,
      }
    })

  const combinedRepairRegions = [...repairsToSimInstall, ...configRepairRegions]
  const repairsToSimInstallAsPlates: GeckoPlate[] = combinedRepairRegions.map(
    (repair, index) => {
      const repairRectInMeters = GeckoComponentRect.CreateFromXYs(
        '',
        repair.start_x,
        repair.start_y,
        repair.end_x,
        repair.end_y,
        binWidth.unit === GeckoMeasurementUnit.Tubes
          ? GeckoMeasurementUnit.Tubes
          : GeckoMeasurementUnit.Feet,
        GeckoMeasurementUnit.Feet
      ).convertTo(GeckoMeasurementUnit.Meters, GeckoMeasurementUnit.Meters)

      const wallNominalThicknessMm = !isNil(repair.nominal_thickness)
        ? GeckoMeasurement.CreateFromValue(
            repair.nominal_thickness,
            GeckoMeasurementUnit.Inches
          ).convertTo(GeckoMeasurementUnit.Millimeters).num
        : undefined

      return GeckoPlate.CreateFromJSON('', index.toString(), {
        type: GeckoFeatureModelType.Plate,
        name: `sim-plate-${index}`,
        feature_geometry: {
          x_start: repairRectInMeters.x.start.num,
          y_start: repairRectInMeters.y.start.num,
          x_end: repairRectInMeters.x.end.num,
          y_end: repairRectInMeters.y.end.num,
        },
        surface_specs: {
          wall_nominal_thickness: wallNominalThicknessMm,
          coating_nominal_thickness: undefined,
        },
        installation_date: undefined,
      })
    }
  )

  return function calculateBinRCA(
    binRect: GeckoComponentRect,
    thickness: GeckoMeasurement
  ) {
    const tmin = calculateBinTminFn(binRect, thickness)

    const thicknessToUse =
      getMostRecentPlateInBin(
        repairsToSimInstallAsPlates,
        binRect,
        inspectionDate.toDate()
      )?.wallNominalThickness ?? thickness

    const outRCA = new Decimal(
      thicknessToUse.convertTo(GeckoMeasurementUnit.Millimeters).num
    )
      .minus(tmin.convertTo(GeckoMeasurementUnit.Millimeters).num)
      .toNumber()
    if (percentageRCA) {
      return GeckoMeasurement.CreateFromValue(
        new Decimal(outRCA)
          .div(tmin.convertTo(GeckoMeasurementUnit.Millimeters).num)
          .mul(100)
          .toNumber(),
        GeckoMeasurementUnit.Percent
      )
    }
    return GeckoMeasurement.CreateFromValue(
      outRCA,
      GeckoMeasurementUnit.Millimeters
    )
  }
}

// -------------------------------------------- Remaining Life --------------------------------------------
export function getRlBinValueFunction(props: {
  inspectionDate: dayjs.Dayjs
  binWidth: GeckoMeasurement
  binHeight: GeckoMeasurement
  tminMethod: GeckoTMinMethod
  weldLines: ReadonlyArray<GeckoVerticalWeld | GeckoHorizontalWeld>
  shellCourses: ReadonlyArray<GeckoShellCourse>
  forecastComponentConfig: ForecastComponentConfig
  assetModel: GeckoAssetModel
  componentId: string
  // defaults to []
  repairsToSimInstall?: OverrideRegion[]
  // defaults to []
  corrosionAugmentationRegions?: CustomCorrosionRegion[]
  findCustomCrForShellCourseFn?: FindCustomCrForShellCourseFn
  // for hacky-access -- TODO CAN-120: Remove me
  componentType: GeckoComponentModelType | undefined
  historicalCorrosionArrestRegions?: (GeckoCoatingRegion | GeckoLinerRegion)[]
}): CalculateBinValueFunction {
  const {
    inspectionDate,
    binWidth,
    binHeight,
    tminMethod,
    weldLines,
    shellCourses,
    forecastComponentConfig,
    assetModel,
    componentId,
    repairsToSimInstall = [],
    corrosionAugmentationRegions = [],
    findCustomCrForShellCourseFn,
    componentType,
    historicalCorrosionArrestRegions,
  } = props

  const binWidthMetric = binWidth.convertTo(GeckoMeasurementUnit.Meters)
  const binHeightMetric = binHeight.convertTo(GeckoMeasurementUnit.Meters)

  const calculateBinRcaFn = getRcaBinValueFunction({
    binWidth: binWidthMetric,
    binHeight: binHeightMetric,
    tminMethod,
    weldLines,
    shellCourses,
    forecastComponentConfig,
    inspectionDate,
    assetModel,
    componentId,
    componentType,
    repairsToSimInstall,
  })

  const calculateBinLossPerYearFn = getLossPerYearBinValueFunction({
    inspectionDate,
    corrosionAugmentationRegions,
    findCustomCrForShellCourseFn,
    assetModel,
    calculateRcaBinValueFn: calculateBinRcaFn,
    forecastComponentConfig,
    historicalCorrosionArrestRegions,
  })

  return function calculateBinRL(
    binRect: GeckoComponentRect,
    thickness: GeckoMeasurement
  ) {
    const rca = calculateBinRcaFn(binRect, thickness).convertTo(
      GeckoMeasurementUnit.Millimeters
    ).num
    const lossPerYear = calculateBinLossPerYearFn(binRect, thickness).convertTo(
      GeckoMeasurementUnit.MillimetersPerYear
    ).num

    // ensure lpy is positive (at least 0), this makes it so a negative loss per year shows the largest RL (not smallest)
    const lossPerYearPositive = Math.max(0, lossPerYear)

    return GeckoMeasurement.CreateFromValue(
      new Decimal(rca).div(lossPerYearPositive).clamp(-10, 150).toNumber(),
      GeckoMeasurementUnit.Years
    )
  }
}

// -------------------------------------------- Thickness By Mean --------------------------------------------
export function getThicknessByMeanBinValueFunction(
  meanThickness: GeckoMeasurement
): CalculateBinValueFunction {
  return function calculateBinLossPerYear(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    binRect: GeckoComponentRect,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    thickness: GeckoMeasurement
  ) {
    return meanThickness.convertTo(GeckoMeasurementUnit.Inches)
  }
}

// -------------------------------------------- Time Since Last Repair --------------------------------------------
export function getTimeSinceLastRepairBinValueFunction(
  inspectionDate: dayjs.Dayjs,
  assetModel: GeckoAssetModel,
  componentId: string,
  forecastComponentConfig: ForecastComponentConfig | undefined
): CalculateBinValueFunction {
  return function calculateBinLossPerYear(
    binRect: GeckoComponentRect,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we need to keep the signature consistent
    thickness: GeckoMeasurement
  ) {
    const { installationDate } = getManufactureSpecsForBin({
      binBoundsToGetSpecsFor: binRect,
      assetModel,
      forecastComponentConfig,
      latestDateToUse: inspectionDate.toDate(),
    })

    if (isNil(installationDate)) {
      GeckoMeasurement.CreateFromValue(Number.NaN, GeckoMeasurementUnit.Years)
    }

    const daysElapsed = inspectionDate.diff(installationDate, 'd')
    const yearsElapsed = new Decimal(daysElapsed).div(365)

    return GeckoMeasurement.CreateFromValue(
      yearsElapsed.toNumber(),
      GeckoMeasurementUnit.Years
    )
  }
}

export function getEmatCombinedCategoryBinValueFunction(props: {
  ematCategoryData: ReadonlyArray<ReadonlyArray<number>>
  differenceFromNominalData: ReadonlyArray<ReadonlyArray<number>>
}): CalculateBinValueFunction {
  const { ematCategoryData, differenceFromNominalData } = props

  return function calculateBinCombinedCategory(
    activeBinRect: GeckoComponentRect,
    thickness: GeckoMeasurement,
    indices?: { x: number; y: number }
  ) {
    if (indices) {
      const { x, y } = indices

      const ematCategoryValue = ematCategoryData[y]?.[x]
      const differenceFromNominalValue = differenceFromNominalData[y]?.[x]

      // New categorical scoring system based on differenceFromNominalValue
      let combinedValue = NaN

      // If only EMAT category exists (no difference from nominal), check for specific values
      if (
        ematCategoryValue !== undefined &&
        !isNaN(ematCategoryValue) &&
        (differenceFromNominalValue === undefined ||
          isNaN(differenceFromNominalValue))
      ) {
        //if the emat category value is 2(invalid bins),set to 0 for SN/V
        //if the emat category value is 3(valid bins but no DFN value),set to 6 for no wastage data available
        //TODO: import in the thickness to see breakdown no wastage to see if it is from lack of thickness or lack of nominal
        combinedValue =
          ematCategoryValue === 2 ? 1 : ematCategoryValue === 3 ? 0 : NaN
      }
      // If differenceFromNominalValue exists, use threshold-based scoring
      else if (
        differenceFromNominalValue !== undefined &&
        !isNaN(differenceFromNominalValue)
      ) {
        if (differenceFromNominalValue > 0) {
          combinedValue = 2 // Greater than 0
        } else if (differenceFromNominalValue > THRESHOLD_MINOR_WASTAGE) {
          combinedValue = 3 // 0 to -0.05
        } else if (differenceFromNominalValue > THRESHOLD_MODERATE_WASTAGE) {
          combinedValue = 4 // -0.05 to -0.2
        } else if (differenceFromNominalValue > THRESHOLD_SEVERE_WASTAGE) {
          combinedValue = 5 // -0.2 to -0.25
        } else {
          combinedValue = 6 // Less than -0.25
        }
      }
      // return score based on the matched value between the two layers

      if (combinedValue >= 0) {
        return GeckoMeasurement.CreateFromValue(
          combinedValue,
          GeckoMeasurementUnit.EmatCustomCategory
        )
      }
    }

    // Fallback: return NaN for bins with no data
    return GeckoMeasurement.CreateFromValue(
      NaN,
      GeckoMeasurementUnit.EmatCustomCategory
    )
  }
}
